import { timingStatusLabel, timeRangeLabel } from "../../utils/time.js";

export const NA_ROOM_ID = "__NA_ROOM__";

export function byId(rows = []) {
  return Object.fromEntries(rows.map((row) => [row.id || row[`${row.kind}Id`], row]));
}

export function findById(rows = [], id) {
  return rows.find((row) => row.id === id || row.teacherId === id || row.subjectId === id || row.classId === id || row.sectionId === id || row.roomId === id);
}

export function enrichRoutine(form, master) {
  const teacher = findById(master.teachers, form.teacherId);
  const subject = findById(master.subjects, form.subjectId);
  const classItem = findById(master.classes, form.classId);
  const section = findById(master.sections, form.sectionId);
  const hasNoRoom = form.roomId === NA_ROOM_ID || !form.roomId || String(form.roomNumber || "").toUpperCase() === "N/A";
  const room = hasNoRoom ? null : findById(master.rooms, form.roomId);

  return {
    ...form,
    days: Array.isArray(form.days) ? form.days : String(form.days || "").split(",").map((day) => day.trim()).filter(Boolean),
    teacherName: teacher?.name || form.teacherName || "",
    subjectName: subject?.name || form.subjectName || "",
    className: classItem?.name || form.className || "",
    sectionName: section?.name || form.sectionName || "",
    roomId: hasNoRoom ? NA_ROOM_ID : form.roomId,
    roomNumber: hasNoRoom ? "N/A" : room?.roomNumber || form.roomNumber || "N/A",
    status: form.status || "active",
    active: form.active !== false
  };
}

export function routineToExportRow(routine) {
  return {
    Day: Array.isArray(routine.days) ? routine.days.join(", ") : routine.day || "",
    Room: routine.roomNumber || "",
    Class: routine.className || "",
    Section: routine.sectionName || "",
    Subject: routine.subjectName || "",
    Teacher: routine.teacherName || "",
    "Schedule IN": routine.scheduledIn || "",
    "Schedule OUT": routine.scheduledOut || "",
    Status: routine.active === false ? "inactive" : routine.status || "active",
    Notes: routine.notes || ""
  };
}

export function logToExportRow(log) {
  return {
    Date: log.date || "",
    Day: log.day || "",
    Room: log.roomNumber || "",
    Class: log.className || "",
    Section: log.sectionName || "",
    Subject: log.subjectName || "",
    Teacher: log.teacherName || "",
    "Scheduled IN": log.scheduledIn || "",
    "Scheduled OUT": log.scheduledOut || "",
    "Actual IN": log.actualIn || "",
    "Actual OUT": log.actualOut || "",
    "Entry Difference": log.entryDifferenceMinutes ?? "",
    "Exit Difference": log.exitDifferenceMinutes ?? "",
    "Entry Status": timingStatusLabel(log, "entry"),
    "Exit Status": timingStatusLabel(log, "exit"),
    "Class Status": log.classStatus || "normal",
    "Suspension Reason": log.suspensionReason || "",
    "Updated By": log.updatedByName || log.updatedBy || ""
  };
}

export function buildSavedLogReportRows(logs = []) {
  return logs.map((log) => ({
    date: log.date || "",
    day: log.day || "",
    routineId: log.routineId || log.logId || log.id || "",
    roomNumber: log.roomNumber || "N/A",
    className: log.className || "",
    sectionName: log.sectionName || "",
    subjectName: log.subjectName || "",
    teacherName: log.teacherName || "",
    teacherId: log.teacherId || "",
    classId: log.classId || "",
    subjectId: log.subjectId || "",
    roomId: log.roomId || "",
    scheduledIn: log.scheduledIn || "",
    scheduledOut: log.scheduledOut || "",
    schedule: timeRangeLabel(log.scheduledIn, log.scheduledOut),
    actualIn: log.actualIn || "",
    actualOut: log.actualOut || "",
    entryStatus: log.entryStatus || "",
    entryStatusLabel: timingStatusLabel(log, "entry"),
    exitStatus: log.exitStatus || "",
    exitStatusLabel: timingStatusLabel(log, "exit"),
    classStatus: log.classStatus || "normal",
    suspensionReason: log.suspensionReason || "",
    lateEntryMinutes: Number(log.lateEntryMinutes || 0),
    earlyEntryMinutes: Number(log.earlyEntryMinutes || 0),
    lateExitMinutes: Number(log.lateExitMinutes || 0),
    earlyExitMinutes: Number(log.earlyExitMinutes || 0),
    updatedByName: log.updatedByName || log.updatedBy || ""
  }));
}

export function summarizeReportRows(rows) {
  return {
    "Total saved records": rows.length,
    "Total IN marked": rows.filter((row) => row.actualIn).length,
    "Total OUT completed": rows.filter((row) => row.actualOut).length,
    "Total pending OUT": rows.filter((row) => row.actualIn && !row.actualOut && row.classStatus !== "suspended").length,
    "Total suspended classes": rows.filter((row) => row.classStatus === "suspended").length,
    "Total late entries": rows.filter((row) => row.entryStatus === "Late Entry").length,
    "Total early entries": rows.filter((row) => row.entryStatus === "Early Entry").length,
    "Total on-time entries": rows.filter((row) => row.entryStatus === "On Time Entry").length,
    "Total late entry minutes": rows.reduce((sum, row) => sum + Number(row.lateEntryMinutes || 0), 0),
    "Total early entry minutes": rows.reduce((sum, row) => sum + Number(row.earlyEntryMinutes || 0), 0),
    "Total late exit minutes": rows.reduce((sum, row) => sum + Number(row.lateExitMinutes || 0), 0),
    "Total early exit minutes": rows.reduce((sum, row) => sum + Number(row.earlyExitMinutes || 0), 0)
  };
}
